import { defineStore } from 'pinia';
import api from '@/common/utils/api';
import type {
  S3_service,
  S3_related_user_key_with_secret,
  S3_related_user_key,
  S3_related_user_info
} from '@schema';
import waitTask from '@/common/utils/waitTask';
import ServerError from '@/common/utils/errors/ServerError';

const API_URL = '/api/v2/s3service';

const handleError = (error, state, $reset) => {
  if (
    error instanceof ServerError
    && error.errorCode
  ) {
    state.isS3Available = !['s3_disabled', 's3_not_found'].includes(error.errorCode);
    state.isS3UserEnabled = error.errorCode !== 's3_user_disabled';
    if (!state.isS3Available || !state.isS3UserEnabled) {
      $reset();
    }
  }

  throw error;
};

type State = {
  config: S3_service | null,
  user: Required<S3_related_user_info>
  isS3Available: boolean | null,
  isS3UserEnabled: boolean,
  domainsS3Status: Record<ID, boolean>,
};

export default defineStore('s3Service', {
  state: (): State => ({
    config: null,
    user: {
      arn: '',
      enabled: true,
      keys: [],
    },
    isS3Available: null,
    isS3UserEnabled: false,
    domainsS3Status: {},
  }),
  getters: {
    getDomainS3Status: (state: State) => (domainId: ID) => state.domainsS3Status[domainId] || false,
  },
  actions: {
    async getS3Service() {
      try {
        const { data } = await api.get<S3_service>(API_URL);
        this.isS3Available = true;
        this.$patch({ config: data });
        return data;
      } catch (error) {
        return handleError(error, this.$state, this.$reset.bind(this));
      }
    },
    async createS3Service() {
      try {
        const { data } = await api.post<S3_service>(API_URL);
        this.config = data;
        return data;
      } catch (error) {
        return handleError(error, this.$state, this.$reset.bind(this));
      }
    },
    async getUser() {
      try {
        const { data } = await api.get<S3_related_user_info>(`${API_URL}/user`);
        this.$patch({ user: data });
        return data;
      } catch (error) {
        return handleError(error, this.$state, this.$reset.bind(this));
      }
    },
    async createKeys(payload) {
      try {
        const { data } = await api.post<S3_related_user_key_with_secret>(`${API_URL}/keys`, payload);
        const { sec_key, ...key } = data;
        this.user.keys.push(key);
        return data;
      } catch (error) {
        return handleError(error, this.$state, this.$reset.bind(this));
      }
    },
    async updateKey(accesskey, payload) {
      try {
        const { data } = await api.put<S3_related_user_key>(`${API_URL}/keys/${accesskey}`, payload);
        const newKey = data[0];
        this.user.keys = this.user.keys.map((key) => (key.acc_key === newKey.acc_key ? newKey : key));
        return data;
      } catch (error) {
        return handleError(error, this.$state, this.$reset.bind(this));
      }
    },
    async deleteKey(accesskey) {
      try {
        await api.delete(`${API_URL}/keys/${accesskey}`);
        this.user.keys = this.user.keys.filter((key) => key.acc_key !== accesskey);
        return true;
      } catch (error) {
        return handleError(error, this.$state, this.$reset.bind(this));
      }
    },
    async getS3ServiceDomainStatus(domainId: ID) {
      const { data } = await api.get(`${API_URL}/domain/${domainId}`);
      this.domainsS3Status = {
        ...this.domainsS3Status,
        [domainId]: data
      };
      return data;
    },
    async setS3ServiceDomainStatus(domainId: ID, enabled) {
      const { data } = await api.put(`${API_URL}/domain/${domainId}`, { enabled });
      await waitTask(data.task_id);
      this.domainsS3Status[domainId] = enabled;
      return data;
    }
  },
});
